import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { reviewAPI, emailAPI } from '../services/api';
import type { Email, EmailCategory } from '../types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select-new';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui';
import { capitalize } from '@/lib/utils';
import { CheckCircle, XCircle, ArrowUpDown, X } from 'lucide-react';
import FeedbackButtons from '../components/FeedbackButtons';
import { CommentSection } from '../components/CommentSection';
import { InfoTooltip } from '../components/InfoTooltip';

const CATEGORIES: EmailCategory[] = [
  'customer_question',
  'product_question',
  'shipping_question',
  'damaged_defective',
  'wrong_item',
  'order_cancellation',
  'return_policy',
  'battery_support',
  'pending_response',
];

interface ReviewQueueProps {
  showAll?: boolean;
}

export default function ReviewQueue({ showAll = false }: ReviewQueueProps) {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedPlatform, setSelectedPlatform] = useState<string>('all');
  const [selectedTimeFilter, setSelectedTimeFilter] = useState<string>('all');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [selectedLimit, setSelectedLimit] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  // Get all filters from URL params on load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    
    const status = params.get('status');
    const category = params.get('category');
    const platform = params.get('platform');
    const timeFilter = params.get('timeFilter');
    const startDate = params.get('startDate');
    const endDate = params.get('endDate');
    const search = params.get('search');
    const limit = params.get('limit');
    const sort = params.get('sort');
    const page = params.get('page');
    
    if (status) setSelectedStatus(status);
    if (category) setSelectedCategory(category);
    if (platform) setSelectedPlatform(platform);
    if (timeFilter) setSelectedTimeFilter(timeFilter);
    if (startDate) setCustomStartDate(startDate);
    if (endDate) setCustomEndDate(endDate);
    if (search) setSearchQuery(search);
    if (limit) setSelectedLimit(limit);
    if (sort && (sort === 'newest' || sort === 'oldest')) setSortOrder(sort);
    if (page) setCurrentPage(parseInt(page) || 1);
  }, []);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (selectedStatus && selectedStatus !== 'all') params.set('status', selectedStatus);
    if (selectedCategory && selectedCategory !== 'all') params.set('category', selectedCategory);
    if (selectedPlatform && selectedPlatform !== 'all') params.set('platform', selectedPlatform);
    if (selectedTimeFilter) params.set('timeFilter', selectedTimeFilter);
    if (customStartDate) params.set('startDate', customStartDate);
    if (customEndDate) params.set('endDate', customEndDate);
    if (searchQuery) params.set('search', searchQuery);
    if (selectedLimit && selectedLimit !== 'all') params.set('limit', selectedLimit);
    if (sortOrder !== 'newest') params.set('sort', sortOrder);
    if (currentPage !== 1) params.set('page', currentPage.toString());
    
    const newUrl = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  }, [selectedStatus, selectedCategory, selectedPlatform, selectedTimeFilter, customStartDate, customEndDate, searchQuery, selectedLimit, sortOrder, currentPage]);

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedCategory('all');
    setSelectedStatus('all');
    setSelectedPlatform('all');
    setSelectedTimeFilter('all');
    setCustomStartDate('');
    setCustomEndDate('');
    setSearchQuery('');
    setSelectedLimit('all');
    setSortOrder('newest');
    setCurrentPage(1);
    setSelectedEmails(new Set());
  };

  // Check if any filters are active
  const hasActiveFilters = selectedCategory !== 'all' || 
                          selectedStatus !== 'all' || 
                          selectedPlatform !== 'all' ||
                          selectedTimeFilter !== 'all' || 
                          customStartDate !== '' || 
                          customEndDate !== '' || 
                          searchQuery !== '' || 
                          selectedLimit !== 'all' || 
                          sortOrder !== 'newest' || 
                          currentPage !== 1;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data: emailsResponse, isLoading, refetch } = useQuery({
    queryKey: ['emails-v2', showAll, selectedCategory, selectedStatus, selectedPlatform, selectedTimeFilter, customStartDate, customEndDate, debouncedSearch, selectedLimit, currentPage],
    queryFn: async () => {
      const params: any = {
        category: selectedCategory && selectedCategory !== 'all' ? selectedCategory : undefined,
        limit: itemsPerPage,
        skip: (currentPage - 1) * itemsPerPage,
      };

      if (selectedTimeFilter === 'custom' && customStartDate) {
        params.start_time = new Date(customStartDate).toISOString();
      }
      if (selectedTimeFilter === 'custom' && customEndDate) {
        params.end_time = new Date(customEndDate).toISOString();
      }

      if (selectedTimeFilter && selectedTimeFilter !== 'custom') {
        const now = new Date();
        let startTime = new Date();
        
        switch (selectedTimeFilter) {
          case '5min':
            startTime = new Date(now.getTime() - 5 * 60 * 1000);
            break;
          case '15min':
            startTime = new Date(now.getTime() - 15 * 60 * 1000);
            break;
          case '1hour':
            startTime = new Date(now.getTime() - 60 * 60 * 1000);
            break;
          case 'today':
            startTime = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            break;
          case 'week':
            startTime = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
        }
        params.start_time = startTime.toISOString();
      }

      if (showAll) {
        // Add response_status filter if selected
        if (selectedStatus && selectedStatus !== 'all') {
          params.response_status = selectedStatus;
        }
        
        // Add platform filter if selected
        if (selectedPlatform && selectedPlatform !== 'all') {
          params.platform = selectedPlatform;
        }
        
        const response = await emailAPI.list(params);
        
        // TODO: Backend search not implemented yet, filter on frontend for now
        let data = response.data.data;
        
        // Universal search filter
        if (debouncedSearch) {
          const searchLower = debouncedSearch.toLowerCase();
          data = data.filter((email: Email) => {
            // Search in multiple fields
            const searchableText = [
              email.extracted_entities?.order_id || '',
              email.extracted_entities?.customer_name || '',
              email.from_address || '',
              email.subject || '',
              email.body || '',
              email.extracted_entities?.jtl_order_data?.buyer_name || '',
              email.extracted_entities?.jtl_order_data?.product?.name || '',
              email.extracted_entities?.jtl_order_data?.product?.sku || '',
              email.extracted_entities?.tracking_number || ''
            ].join(' ').toLowerCase();
            
            return searchableText.includes(searchLower);
          });
        }
        
        // Return paginated response with filtered data
        return {
          data: data,
          total: response.data.total, // Use backend total (search filtering breaks this, but acceptable for now)
          page: response.data.page,
          limit: response.data.limit,
          total_pages: response.data.total_pages
        };
      } else {
        // Review Queue: Show pending_review, approved, and rejected emails
        // Add response_status filter if selected
        if (selectedStatus && selectedStatus !== 'all') {
          params.response_status = selectedStatus;
        }
        
        // Add platform filter if selected
        if (selectedPlatform && selectedPlatform !== 'all') {
          params.platform = selectedPlatform;
        }
        
        const response = await reviewAPI.getReviewQueue(params);
        let data = response.data.data; // Extract emails from paginated response
        
        // Universal search filter
        if (debouncedSearch) {
          const searchLower = debouncedSearch.toLowerCase();
          data = data.filter((email: Email) => {
            // Search in multiple fields
            const searchableText = [
              email.extracted_entities?.order_id || '',
              email.extracted_entities?.customer_name || '',
              email.from_address || '',
              email.subject || '',
              email.body || '',
              email.extracted_entities?.jtl_order_data?.buyer_name || '',
              email.extracted_entities?.jtl_order_data?.product?.name || '',
              email.extracted_entities?.jtl_order_data?.product?.sku || '',
              email.extracted_entities?.tracking_number || ''
            ].join(' ').toLowerCase();
            
            return searchableText.includes(searchLower);
          });
        }
        
        // Return paginated response with filtered data
        return {
          data: data,
          total: response.data.total,
          page: response.data.page,
          limit: response.data.limit,
          total_pages: response.data.total_pages
        };
      }
    },
  });

  // Extract emails and total from paginated response
  const emails = emailsResponse?.data || [];
  const totalEmails = emailsResponse?.total || 0;

  const handleBulkApprove = async () => {
    if (selectedEmails.size === 0) return;
    
    try {
      await reviewAPI.bulkApprove(Array.from(selectedEmails), 'admin');
      toast.success(`Approved ${selectedEmails.size} emails`);
      setSelectedEmails(new Set());
      refetch();
    } catch (error) {
      toast.error('Failed to approve emails');
    }
  };

  const handleBulkReject = async () => {
    if (selectedEmails.size === 0) return;
    
    try {
      await reviewAPI.bulkReject(Array.from(selectedEmails), 'admin');
      toast.success(`Rejected ${selectedEmails.size} emails`);
      setSelectedEmails(new Set());
      refetch();
    } catch (error) {
      toast.error('Failed to reject emails');
    }
  };

  const handleBulkReprocess = async () => {
    if (selectedEmails.size === 0) return;
    
    try {
      await reviewAPI.bulkReprocess(Array.from(selectedEmails));
      toast.success(`Reprocessed ${selectedEmails.size} emails`);
      setSelectedEmails(new Set());
      refetch();
    } catch (error) {
      toast.error('Failed to reprocess emails');
    }
  };

  const handleQuickApprove = async (id: string) => {
    try {
      await reviewAPI.approve(id, 'admin');
      toast.success('Email approved');
      refetch();
    } catch (error) {
      toast.error('Failed to approve email');
    }
  };

  const handleQuickApproveAndSend = async (id: string) => {
    try {
      await reviewAPI.approveAndSend(id, 'admin');
      toast.success('Email approved and sent');
      refetch();
    } catch (error) {
      toast.error('Failed to approve and send email');
    }
  };

  const handleQuickReject = async (id: string) => {
    try {
      await reviewAPI.reject(id, 'admin');
      toast.success('Email rejected');
      refetch();
    } catch (error) {
      toast.error('Failed to reject email');
    }
  };

  const handleRegenerate = async (id: string) => {
    try {
      toast.loading('Regenerating response...', { id: 'regenerate' });
      await reviewAPI.regenerate(id);
      toast.success('Response regenerated successfully', { id: 'regenerate' });
      refetch();
    } catch (error) {
      toast.error('Failed to regenerate response', { id: 'regenerate' });
    }
  };

  const handleBulkRegenerate = async () => {
    try {
      toast.loading(`Regenerating ${selectedEmails.size} responses...`, { id: 'bulk-regenerate' });
      await reviewAPI.bulkRegenerate(Array.from(selectedEmails));
      toast.success(`Successfully regenerated ${selectedEmails.size} responses`, { id: 'bulk-regenerate' });
      setSelectedEmails(new Set());
      refetch();
    } catch (error) {
      toast.error('Failed to regenerate responses', { id: 'bulk-regenerate' });
    }
  };

  const handleReprocess = async (id: string) => {
    try {
      await reviewAPI.reprocess(id);
      toast.success('Email moved back to review queue');
      refetch();
    } catch (error) {
      toast.error('Failed to reprocess email');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Apply sort order
  const sortedEmails = sortOrder === 'oldest' 
    ? [...(emails || [])].reverse() 
    : emails;

  // Backend pagination - no slicing needed
  const totalPages = Math.ceil(totalEmails / itemsPerPage);
  const paginatedEmails = sortedEmails;
  
  // Display range
  const displayStart = (currentPage - 1) * itemsPerPage + 1;
  const displayEnd = Math.min(currentPage * itemsPerPage, totalEmails);

  return (
    <div className="h-full bg-white flex flex-col">
      {/* Header */}
      <div className="border-b border-gray-200 px-8 py-4">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-semibold text-gray-900">
            {showAll ? 'All Emails' : 'Mailbox'}
          </h1>
          {selectedEmails.size > 0 && (
            <div className="flex items-center gap-2">
              {(selectedStatus === 'pending_review' || selectedStatus === 'all') && (
                <>
                  <Button 
                    onClick={handleBulkRegenerate}
                    variant="outline"
                    size="sm"
                    className="border-purple-200 text-purple-700 hover:bg-purple-50"
                  >
                    Regenerate ({selectedEmails.size})
                  </Button>
                  <Button 
                    onClick={handleBulkApprove}
                    variant="outline"
                    size="sm"
                    className="border-green-200 text-green-700 hover:bg-green-50"
                  >
                    Approve ({selectedEmails.size})
                  </Button>
                  <Button 
                    onClick={handleBulkReject}
                    variant="outline"
                    size="sm"
                    className="border-red-200 text-red-700 hover:bg-red-50"
                  >
                    Reject ({selectedEmails.size})
                  </Button>
                </>
              )}
              {selectedStatus === 'rejected' && (
                <Button 
                  onClick={handleBulkReprocess}
                  variant="outline"
                  size="sm"
                  className="border-blue-200 text-blue-700 hover:bg-blue-50"
                >
                  Reprocess ({selectedEmails.size})
                </Button>
              )}
            </div>
          )}
        </div>
        
        {/* Search */}
        <div className="relative">
          <Input
            type="text"
            placeholder="Search by order ID, customer name, email, or content..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full"
          />
        </div>
      </div>

      {/* Subheader */}
      <div className="border-b border-gray-200 px-8 py-3 bg-gray-50">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {showAll ? 'All Emails' : 'To-Do'}
          </h2>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSortOrder(sortOrder === 'newest' ? 'oldest' : 'newest')}
            >
              <ArrowUpDown className="h-4 w-4 mr-1" />
              {sortOrder === 'newest' ? 'Newest First' : 'Oldest First'}
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="border-b border-gray-200 px-8 py-3 flex items-center gap-2 overflow-x-auto">
        <div className="flex items-center gap-1">
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="h-8 w-[160px]">
              <SelectValue placeholder="Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {capitalize(cat)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <InfoTooltip content="Filter emails by category" />
        </div>
        
        <div className="flex items-center gap-1">
          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger className="h-8 w-[160px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending_review">Pending Review</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              {showAll && <SelectItem value="sent">Sent</SelectItem>}
              {showAll && <SelectItem value="closed">Closed</SelectItem>}
            </SelectContent>
          </Select>
          <InfoTooltip content="Filter by email status" />
        </div>
        
        <div className="flex items-center gap-1">
          <Select value={selectedPlatform} onValueChange={setSelectedPlatform}>
            <SelectTrigger className="h-8 w-[140px]">
              <SelectValue placeholder="Platform" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Platforms</SelectItem>
              <SelectItem value="amazon">Amazon</SelectItem>
              <SelectItem value="ebay">eBay</SelectItem>
            </SelectContent>
          </Select>
          <InfoTooltip content="Filter by platform (Amazon or eBay)" />
        </div>
        
        <div className="flex items-center gap-1">
          <Select value={selectedTimeFilter} onValueChange={(value) => {
            setSelectedTimeFilter(value);
            if (value !== 'custom') {
              setCustomStartDate('');
              setCustomEndDate('');
            }
          }}>
            <SelectTrigger className="h-8 w-[160px]">
              <SelectValue placeholder="Timeframe" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="5min">Past 5 min</SelectItem>
              <SelectItem value="15min">Past 15 min</SelectItem>
              <SelectItem value="1hour">Past hour</SelectItem>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">Past week</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
          <InfoTooltip content="Filter by time received" />
        </div>
        
        <div className="flex items-center gap-1">
          <Select value={selectedLimit} onValueChange={setSelectedLimit}>
            <SelectTrigger className="h-8 w-[140px]">
              <SelectValue placeholder="Show All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Show All</SelectItem>
              <SelectItem value="25">Last 25</SelectItem>
              <SelectItem value="50">Last 50</SelectItem>
              <SelectItem value="100">Last 100</SelectItem>
            </SelectContent>
          </Select>
          <InfoTooltip content="Limit number of emails" />
        </div>
        
        {selectedTimeFilter === 'custom' && (
          <>
            <Input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              className="h-8 w-auto"
            />
            <span className="text-sm text-gray-500">to</span>
            <Input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              className="h-8 w-auto"
            />
          </>
        )}

        {hasActiveFilters && (
          <Button
            variant="outline"
            size="sm"
            onClick={clearAllFilters}
            className="h-8 border-red-200 text-red-700 hover:bg-red-50"
          >
            <X className="h-4 w-4 mr-1" />
            Clear
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
            <tr>
              <th className="w-10 px-4 py-3">
                {paginatedEmails && paginatedEmails.length > 0 && (
                  <Checkbox
                    checked={paginatedEmails.every((email: Email) => selectedEmails.has(email.id))}
                    onCheckedChange={(checked: boolean) => {
                      if (checked) {
                        const newSelected = new Set(selectedEmails);
                        paginatedEmails.forEach((email: Email) => newSelected.add(email.id));
                        setSelectedEmails(newSelected);
                      } else {
                        const newSelected = new Set(selectedEmails);
                        paginatedEmails.forEach((email: Email) => newSelected.delete(email.id));
                        setSelectedEmails(newSelected);
                      }
                    }}
                  />
                )}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
              {!showAll && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={showAll ? 7 : 8} className="px-4 py-8 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : paginatedEmails?.length === 0 ? (
              <tr>
                <td colSpan={showAll ? 7 : 8} className="px-4 py-8 text-center text-gray-500">
                  No emails found
                </td>
              </tr>
            ) : (
              paginatedEmails?.map((email: Email) => (
                <tr
                  key={email.id}
                  onClick={() => {
                    const currentParams = new URLSearchParams(window.location.search);
                    const returnUrl = currentParams.toString() ? `?${currentParams.toString()}` : '';
                    navigate(`/review/${email.id}${returnUrl}`);
                  }}
                  className="hover:bg-gray-50 cursor-pointer"
                >
                  <td className="px-4 py-3">
                    <Checkbox
                      checked={selectedEmails.has(email.id)}
                      onCheckedChange={(checked: boolean) => {
                        const newSelected = new Set(selectedEmails);
                        if (checked) {
                          newSelected.add(email.id);
                        } else {
                          newSelected.delete(email.id);
                        }
                        setSelectedEmails(newSelected);
                      }}
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {new Date(email.received_at || email.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-900">{capitalize(email.category?.replace(/_/g, ' ') || 'Query')}</span>
                      <Badge 
                        variant="secondary"
                        className={
                          (email.platform || 'amazon') === 'ebay' 
                            ? 'bg-blue-100 text-blue-800 hover:bg-blue-100' 
                            : 'bg-orange-100 text-orange-800 hover:bg-orange-100'
                        }
                      >
                        {(email.platform || 'amazon').toUpperCase()}
                      </Badge>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {email.from_address?.split('<')[0].trim() || email.from_address?.split('@')[0] || 'Unknown'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 max-w-md truncate">
                    {email.subject}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <Badge 
                      variant={
                        email.response_status === 'approved' ? 'default' :
                        email.response_status === 'rejected' ? 'destructive' :
                        email.response_status === 'sent' ? 'default' :
                        email.response_status === 'closed' ? 'secondary' :
                        'secondary'
                      }
                      className={
                        email.response_status === 'approved' ? 'bg-green-100 text-green-800 hover:bg-green-100' :
                        email.response_status === 'sent' ? 'bg-blue-100 text-blue-800 hover:bg-blue-100' :
                        email.response_status === 'pending_review' ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100' :
                        email.response_status === 'closed' ? 'bg-gray-100 text-gray-800 hover:bg-gray-100' :
                        ''
                      }
                    >
                      {email.response_status === 'pending_review' ? 'Pending' :
                       email.response_status === 'approved' ? 'Approved' :
                       email.response_status === 'rejected' ? 'Rejected' :
                       email.response_status === 'sent' ? 'Sent' :
                       email.response_status === 'closed' ? 'Closed' :
                       capitalize(email.response_status || 'N/A')}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {email.extracted_entities?.order_id || '-'}
                  </td>
                  {!showAll && (
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1">
                        {email.response_status === 'pending_review' && (
                          <>
                            <Button
                            size="sm"
                            variant="outline"
                            className="border-green-200 text-green-700 hover:bg-green-50"
                            onClick={() => handleQuickApprove(email.id)}
                          >
                            <CheckCircle className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-200 text-red-700 hover:bg-red-50"
                            onClick={() => handleQuickReject(email.id)}
                          >
                            <XCircle className="h-3 w-3" />
                          </Button>
                        </>
                      )}
                      {email.response_status === 'rejected' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-blue-200 text-blue-700 hover:bg-blue-50"
                          onClick={() => handleReprocess(email.id)}
                        >
                          Reprocess
                        </Button>
                      )}
                    </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer / Pagination */}
      <div className="border-t border-gray-200 px-8 py-3 flex items-center justify-between bg-gray-50">
        <div className="text-sm text-gray-600">
          Showing {displayStart} - {displayEnd} of {totalEmails.toLocaleString()}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          
          {totalPages > 1 && Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const pageNum = i + 1;
            return (
              <Button
                key={pageNum}
                variant={currentPage === pageNum ? "default" : "outline"}
                size="sm"
                onClick={() => setCurrentPage(pageNum)}
                className={currentPage === pageNum ? "bg-blue-600 text-white" : ""}
              >
                {pageNum}
              </Button>
            );
          })}
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
